// Pure filter-logic helpers shared by CardFilterBar and any consumer
// that needs to match cards directly (e.g. when applying the bar's
// predicate to per-item entities like auction listings).

import { extractKeywordAbilities } from '../game/sorceryKeywords';
import { spellcasterElements } from '../game/engine/automation/ops/spellcaster';
import { isFoilFinish } from './foil';
import { k } from '../../localization/t';

// Per-element spellcaster filter labels (Air / Earth / Fire / Water
// Spellcaster). Element-restricted spellcasters (a mono "Fire Spellcaster" or
// a multi "Earth and Water Spellcaster") get one entry per element so a player
// can filter for "Air Spellcaster", or "Air" + "Water" together to find an
// air-and-water spellcaster. A full Spellcaster keeps only the bare keyword.
const SPELLCASTER_ELEMENT_LABELS = { air: 'Air', earth: 'Earth', fire: 'Fire', water: 'Water' };
export const SPELLCASTER_ELEMENT_KEYWORDS = Object.values(SPELLCASTER_ELEMENT_LABELS)
  .map((el) => `${el} Spellcaster`);

export const SET_ID_MAP = {
  gothic: 'Gothic',
  arthurian: 'Arthurian Legends',
  beta: 'Beta',
  dragonlord: 'Dragonlord',
};

export const NUMERIC_OPERATORS = [
  { value: '=', label: k('Equals') },
  { value: '!=', label: k('Not equal') },
  { value: '<', label: k('Less than') },
  { value: '<=', label: k('At most') },
  { value: '>', label: k('Greater than') },
  { value: '>=', label: k('At least') },
];

export const UMBRELLAS = {
  Evil: { subtypes: new Set(['Demon', 'Undead', 'Monster']) },
  Royalty: { namePatterns: [/\bKing\b/i, /\bQueene?\b/i, /\bPrince\b/i, /\bPrincess\b/i] },
  Knight: { namePatterns: [/\bKnights?\b/i, /\bSir\b/i, /\bDame\b/i] },
};

export function cardMatchesUmbrella(card, umbrella) {
  const def = UMBRELLAS[umbrella];
  if (!def) return false;
  if (def.subtypes) return (card.traits || []).some((t) => def.subtypes.has(t));
  if (def.namePatterns) return def.namePatterns.some((re) => re.test(card.name));
  return false;
}

export function parseStatNumber(stat) {
  if (stat === null || stat === undefined || stat === '') return null;
  const n = parseInt(stat, 10);
  return Number.isFinite(n) ? n : null;
}

export function compareNum(a, op, b) {
  switch (op) {
    case '=': return a === b;
    case '!=': return a !== b;
    case '<': return a < b;
    case '<=': return a <= b;
    case '>': return a > b;
    case '>=': return a >= b;
    default: return true;
  }
}

export function matchesNumeric(stat, filter) {
  if (!filter || filter.value === null || filter.value === undefined) return true;
  if (stat === null) return false;
  return compareNum(stat, filter.op, filter.value);
}

export function createEmptyFilterState() {
  return {
    searchQuery: '',
    elementFilters: new Set(),
    typeFilters: new Set(),
    setFilters: new Set(),
    rarityFilters: new Set(),
    keywordFilters: new Set(),
    subtypeFilters: new Set(),
    umbrellaFilters: new Set(),
    artistFilters: new Set(),
    foilFilter: 'all',
    cardScope: 'all',
    rulesQuery: '',
    flavorQuery: '',
    costFilter: { op: '=', value: null },
    attackFilter: { op: '=', value: null },
    defenseFilter: { op: '=', value: null },
    lifeFilter: { op: '=', value: null },
    anyThresholdFilter: { op: '=', value: null },
    airThresholdFilter: { op: '=', value: null },
    earthThresholdFilter: { op: '=', value: null },
    fireThresholdFilter: { op: '=', value: null },
    waterThresholdFilter: { op: '=', value: null },
  };
}

// Build a card -> Set<keyword-name> lookup plus a sorted option list for
// the Keyword multi-select. Cached by the cards array reference so the
// O(n) walk only runs when the catalog swaps.
export function buildKeywordIndex(cards) {
  const index = new Map();
  const allKeywords = new Set();
  for (const card of cards) {
    const text = card.functional_text || card.functional_text_plain || '';
    const found = extractKeywordAbilities(text);
    const names = new Set();
    for (const { keyword } of found) {
      names.add(keyword);
      allKeywords.add(keyword);
    }
    // Element-restricted spellcasters get a per-element entry. They ALSO need
    // the bare "Spellcaster" keyword added here: extractKeywordAbilities reads
    // "Fire Spellcaster" / "Earth and Water Spellcaster" as one unrecognized
    // token, so it never tags them as Spellcaster on its own (they would
    // otherwise be missing from the generic Spellcaster filter entirely).
    const els = spellcasterElements(card);
    if (els) {
      names.add('Spellcaster');
      allKeywords.add('Spellcaster');
      for (const el of els) {
        const label = `${SPELLCASTER_ELEMENT_LABELS[el]} Spellcaster`;
        names.add(label);
        allKeywords.add(label);
      }
    }
    if (names.size === 0) continue;
    index.set(card.unique_id, names);
  }
  const options = Array.from(allKeywords)
    .map((keyword) => ({ value: keyword, label: keyword }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return { index, options };
}

export function buildSubtypeOptions(cards) {
  const categoriesBySubtype = new Map();
  for (const card of cards) {
    const cat = card._sorceryCategory || (card.type === 'Site' ? 'Site' : card.type === 'Avatar' ? 'Avatar' : 'Spell');
    for (const trait of card.traits || []) {
      if (!trait) continue;
      if (!categoriesBySubtype.has(trait)) categoriesBySubtype.set(trait, new Set());
      categoriesBySubtype.get(trait).add(cat);
    }
  }
  const GROUP_LABELS = { Spell: 'Spell subtypes', Site: 'Site subtypes', Avatar: 'Avatar subtypes', Multiple: 'Multiple categories' };
  const GROUP_ORDER = ['Spell', 'Site', 'Avatar', 'Multiple'];
  const opts = [];
  for (const [subtype, cats] of categoriesBySubtype.entries()) {
    const group = cats.size > 1 ? 'Multiple' : Array.from(cats)[0];
    opts.push({ value: subtype, label: subtype, _group: group, group: GROUP_LABELS[group] || group });
  }
  opts.sort((a, b) => {
    const ai = GROUP_ORDER.indexOf(a._group);
    const bi = GROUP_ORDER.indexOf(b._group);
    if (ai !== bi) return ai - bi;
    return a.label.localeCompare(b.label);
  });
  return opts;
}

export function buildArtistOptions(cards) {
  const all = new Set();
  for (const card of cards) {
    for (const printing of card.printings || []) {
      for (const a of printing.artists || []) {
        if (a) all.add(a);
      }
    }
  }
  return Array.from(all)
    .map((s) => ({ value: s, label: s }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

// Build a predicate closing over the current filter state + keyword
// index. Covers every card-level filter (search, pills, numeric stats,
// thresholds, text searches) plus optional scope when scopeData is
// supplied. Foiling (`finish`) is per-entry so it gets its own helper.
export function buildCardMatcher(state, { keywordIndex, scopeData } = {}) {
  const {
    searchQuery, elementFilters, typeFilters, setFilters, rarityFilters,
    keywordFilters, subtypeFilters, umbrellaFilters, artistFilters, cardScope,
    rulesQuery, flavorQuery,
    costFilter, attackFilter, defenseFilter, lifeFilter,
    anyThresholdFilter, airThresholdFilter, earthThresholdFilter,
    fireThresholdFilter, waterThresholdFilter,
  } = state;

  const q = searchQuery.length >= 2 ? searchQuery.toLowerCase() : '';
  const setIds = setFilters.size > 0
    ? new Set([...setFilters].map((s) => SET_ID_MAP[s]).filter(Boolean))
    : null;
  const rulesQ = rulesQuery.trim().toLowerCase();
  const flavorQ = flavorQuery.trim().toLowerCase();

  const deckIds = scopeData?.deckCardIds;
  const ownedMap = scopeData?.ownedMap;

  return (card) => {
    if (!card) return false;

    if (cardScope === 'deck' && deckIds) {
      if (!deckIds.has(card.unique_id)) return false;
    } else if (cardScope === 'owned' && ownedMap) {
      if ((ownedMap.get(card.unique_id) || 0) <= 0) return false;
    }

    if (q && !card.name.toLowerCase().includes(q)) return false;

    if (elementFilters.size > 0) {
      if (!card.elements?.some((e) => elementFilters.has(e.name))) return false;
    }
    if (typeFilters.size > 0 && !typeFilters.has(card.type)) return false;
    if (setIds) {
      if (!card.printings?.some((p) => setIds.has(p.set_id))) return false;
    }
    if (rarityFilters.size > 0 && !rarityFilters.has(card.rarity)) return false;

    if (keywordFilters.size > 0) {
      const cardKeywords = keywordIndex?.get(card.unique_id);
      if (!cardKeywords) return false;
      // Element-spellcaster filters ("Air Spellcaster", ...) use AND: selecting
      // Air + Water matches only a card that is BOTH (an air-and-water
      // spellcaster). Every other keyword stays OR (any selected one matches).
      const elementCasters = [];
      const others = [];
      for (const kw of keywordFilters) {
        if (kw !== 'Spellcaster' && kw.endsWith(' Spellcaster')) elementCasters.push(kw);
        else others.push(kw);
      }
      for (const kw of elementCasters) if (!cardKeywords.has(kw)) return false;
      if (others.length > 0) {
        let hit = false;
        for (const kw of others) {
          if (cardKeywords.has(kw)) { hit = true; break; }
        }
        if (!hit) return false;
      }
    }

    if (subtypeFilters.size > 0) {
      if (!(card.traits || []).some((t) => subtypeFilters.has(t))) return false;
    }

    if (umbrellaFilters.size > 0) {
      let hit = false;
      for (const u of umbrellaFilters) {
        if (cardMatchesUmbrella(card, u)) { hit = true; break; }
      }
      if (!hit) return false;
    }

    if (rulesQ) {
      const text = (card.functional_text_plain || card.functional_text || '').toLowerCase();
      if (!text.includes(rulesQ)) return false;
    }
    if (flavorQ) {
      const hit = (card.printings || []).some((p) =>
        (p.flavor_text || '').toLowerCase().includes(flavorQ) ||
        (p.type_text || '').toLowerCase().includes(flavorQ),
      );
      if (!hit) return false;
    }

    if (artistFilters.size > 0) {
      const hit = (card.printings || []).some((p) =>
        (p.artists || []).some((a) => artistFilters.has(a)),
      );
      if (!hit) return false;
    }

    if (!matchesNumeric(parseStatNumber(card.cost), costFilter)) return false;
    if (!matchesNumeric(parseStatNumber(card.power), attackFilter)) return false;
    if (!matchesNumeric(parseStatNumber(card.defense), defenseFilter)) return false;
    if (!matchesNumeric(parseStatNumber(card.health), lifeFilter)) return false;

    if (anyThresholdFilter.value !== null) {
      const ths = [
        card.airThreshold || 0, card.earthThreshold || 0,
        card.fireThreshold || 0, card.waterThreshold || 0,
      ];
      if (!ths.some((t) => compareNum(t, anyThresholdFilter.op, anyThresholdFilter.value))) return false;
    }
    if (!matchesNumeric(card.airThreshold || 0, airThresholdFilter)) return false;
    if (!matchesNumeric(card.earthThreshold || 0, earthThresholdFilter)) return false;
    if (!matchesNumeric(card.fireThreshold || 0, fireThresholdFilter)) return false;
    if (!matchesNumeric(card.waterThreshold || 0, waterThresholdFilter)) return false;

    return true;
  };
}

// Per-entry foiling matcher. Separate because foiling lives on the
// printing/entry, not the card. Auction listings and deck-editor
// per-foiling tiles apply this after buildCardMatcher.
export function buildFoilingMatcher(state) {
  const { foilFilter } = state;
  if (!foilFilter || foilFilter === 'all') return () => true;
  if (foilFilter === 'standard') {
    return (foiling) => !isFoilFinish(foiling || 'S');
  }
  if (foilFilter === 'foil') {
    return (foiling) => isFoilFinish(foiling || 'S');
  }
  return () => true;
}
